import { Component, HostListener, OnInit } from '@angular/core';
import { Subscription, catchError, forkJoin, of } from 'rxjs';
import { localDB } from './shared/helpers/localStorage';
import { AccessService } from './core/services/access.service';
import { AuthService } from './core/services/auth.service';
import { PreferencesService } from './modules/my-board/service/preferences-service.service';
import { IndexedDBDexieService } from './core/indexedDB/service/indexed-db-dexie.service';
import { Router } from '@angular/router';
import { REFRESH_INDEXED } from './core/indexedDB/helpers/enums.enums';
import { SwPush } from '@angular/service-worker';
import { NotificationsService } from './core/notifications/services/notifications.service';
import { environment } from 'src/environments/environment';
import { CacheService } from './shared/services/cache.service';
import { CookiesService } from './core/services/cookies.service';
import config from 'devextreme/core/config';
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})

export class AppComponent implements OnInit {
  title = 'smart-web-system';
  subscription: Subscription;
  acceptedDomains: string[] = ['materiagris', 'dev', 'qa']
  public readonly VAPID_PUBLIC_KEY = 'BMYaMPu1sLX5PVA_pL5XlD8LUSbrGHHXqsJDAnA0sqxNfcH4hJ2KSk8exZKg_ANJcVCvQpZk1_zX5fek4aEiPO8';
  private readonly keyCombination: Set<string> = new Set();

  constructor(
    private readonly _accessService: AccessService,
    private readonly _authService: AuthService,
    private readonly _preferencesService: PreferencesService,
    private readonly _router: Router,
    private readonly _swPush: SwPush,
    private readonly _notificationService: NotificationsService,
    private readonly _dexieDB: IndexedDBDexieService,
    private readonly _cacheService: CacheService,
    private readonly _cookiesService: CookiesService,
  ) {
    this._dexieDB.initIndexDB()
    this.firstBackendConsult();
    this.itemsNavigationExist();
  }

  ngOnInit(): void {
    this._dexieDB.initIndexDB();
    this.firstBackendConsult();
    this.itemsNavigationExist();
    
    // Llamada ÚNICA al inicio para obtener estado de módulos
    this.initializeActiveModules();
    
    this._router.events.subscribe((data: any) => {
      if (!this._authService.isAuthenticated()) {
        let url = data.url;
        if (url && !url.includes('auth') && data.url != '/') {
          this._accessService.redirectionUrl$.next(url)
        } else {
          this._accessService.redirectionUrl$.next(null)
        }
      }
    });
    this.subscribeToNotifications();
    config({
      licenseKey: environment.devexpress_api_key,
    });
  }

  private initializeActiveModules(): void {
    if (this._authService.isAuthenticated()) {
      this._accessService.getModules().pipe(
        catchError(() => of({ data: [] }))
      ).subscribe((response: any) => {
        if (response?.data) {
          localDB.setItem('Active-Modules', JSON.stringify(response));
        }
      });
    }
  }

  firstBackendConsult() {
    forkJoin([
      this._accessService.newBackendTest().pipe(catchError(e => of({
        "data": 0,
        "isSuccess": false,
        "message": '¡Api no respondeeeeee!',
        "code": 500
      })))
    ]).subscribe((resp) => {
      if (resp.some(objeto => !objeto.isSuccess)) {
        resp.forEach(async (api) => {
          if (!api.isSuccess) {
            let message = `%c${api.message}`;
            console.log(message, "color: red; font-size: 18px; background: yellow");
            if (this._authService.isAuthenticated()) {
              await this._cacheService.refreshAllValues();
              if (this._cookiesService.cookieNameEnv) {
                this._cookiesService.deleteCookie(this._cookiesService.cookieNameEnv);
              }
              localStorage.clear();
              window.location.reload();
            }
          }
        })
      }
    });
  }

  itemsNavigationExist() {
    if (this._authService.isAuthenticated()) {
      this._preferencesService.setBoardConfig().subscribe()
      if (localDB.getItem('aNavigationItem') && localDB.getItem('aSlugPermissions')) {
        let aNavigationItem = JSON.parse(localDB.getItem('aNavigationItem'));
        if (aNavigationItem)
          this._accessService.setNavigationItems(aNavigationItem);
        let aSlugPermissions = JSON.parse(localDB.getItem('aSlugPermissions'));
        if (aSlugPermissions) this._accessService.setPermissions(aSlugPermissions);
      } else {
        this._accessService.routeLogin();
      }
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    this.keyCombination.add(event.key);

    const isWindows = navigator.platform.includes('Win');

    const isCtrlPressed = isWindows ? this.keyCombination.has('Control') : this.keyCombination.has('Meta');
    const isShiftPressed = this.keyCombination.has('Shift');
    const isRPressed = event.key === 'r' || event.code === 'KeyR';

    if (isCtrlPressed && isShiftPressed && isRPressed) {
      event.preventDefault();
      this.handleKeyCombination();
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent) {
    this.keyCombination.delete(event.key);
  }

  private async handleKeyCombination() {
    localDB.cleanCache(['horario', 'c-my-marks', 'c-red-button', 'c-extemp-marks', 'c-assistance', 'c-requests', 'celebrate-banner', 'c-tareo', 'c-requests-treasury']);

    forkJoin([
      this._dexieDB.refreshAllByTypeRefresh(REFRESH_INDEXED.EVERY_REFRESH),
      this._dexieDB.refreshAllByTypeRefresh(REFRESH_INDEXED.EVERY_DAY)
    ]).subscribe(() => {
      location.reload();
    });
  }

  subscribeToNotifications() {
    if (environment.activePush) {
      let userID = Number(localDB.getItem('idUsuario'))

      this._swPush.requestSubscription({
        serverPublicKey: this.VAPID_PUBLIC_KEY
      }).then(sub => {
        const token = JSON.parse(JSON.stringify(sub));
        localDB.setItem("pushtoken", JSON.stringify(sub));

        if (token) {
          this._notificationService.savePushToken({ sEndpoint: token.endpoint, sP256dh: token.keys.p256dh, sAuth: token.keys.auth, nId_Usuario: userID ? userID : undefined }).subscribe({
            next: (resp) => {
              //TODO BIEN, NO CONFUNDIR CON UN TO DO >:c
            },
            error: e => {
              console.warn(e.error.message)
            }
          })
        }
      }).catch(err => console.error('UPS:(', err))
    }
  }
}
